import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Tabs, Flex, Space } from "antd"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import styles from "../Calendar.module.css"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { DEFAULT_RANGE, RANGE_OPTIONS } from "../constants"
import { Text } from "ui/Typography"
import useActiveGuide from "hooks/useActiveClient"

const Header = ({ params, onChangeFilters, handleJournalClick, setIsScheduleTimeOffModalOpen }) => {
  const { isMobile, isTablet } = useDevice()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [active] = useActiveGuide()
  const date = useMemo(() => (params.date ? global.dateTime(params.date) : null), [params.date])

  useEffect(() => {
    if (!params.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
  }, [params.range]) //eslint-disable-line

  if (!params.range) return null

  const handleClick = () => {
    navigate(routes.guideBookingNewPath())
  }

  return (
    <Flex vertical>
      <Flex className={`px-3xl py-s ${styles.headerLegend}`} wrap="wrap">
        <Text className={styles.headerLegendLabel}>{t("calendar.legend")}:</Text>
        <Space className={styles.headerLegendItem}>
          <span className={styles.headerLegendPaid} />
          <Text className={styles.headerLegendText}>{t("calendar.legend_paid")}</Text>
        </Space>
        <Space className={styles.headerLegendItem}>
          <span className={styles.headerLegendOutstanding} />
          <Text className={styles.headerLegendText}>{t("calendar.legend_outstanding")}</Text>
        </Space>
      </Flex>
      <Flex gap={10} align="center" justify="space-between" className={`px-xl py-m ${styles.calendarActionsWrapper}`} wrap="wrap">
        <DateSwitcher
          className={`${styles.calendarSwitcher}`}
          value={date}
          defaultValue={global.dateTime().startOf("date")}
          name="date"
          onChange={onChangeFilters}
          type={params.range}
          isCompleteDateRange={true}
        />
        <div className={styles.tabsContainer}>
          <Tabs
            activeKey={params.range}
            onChange={(key) => onChangeFilters({ target: { name: "range", value: key } })}
            className={styles.antDTabs}
          >
            {Object.entries(RANGE_OPTIONS).map(([range, label]) => (
              <Tabs.TabPane tab={label} key={range} />
            ))}
          </Tabs>
        </div>
        <Flex gap={10} className={`${styles.calendarActions} ml-auto`}>
          <Button
            type="primary"
            className={`${styles.newBooking} px-m py-s`}
            size="large"
            onClick={active ? handleClick : undefined}
            disabled={!active}
            aria-disabled={!active}
          >
            {t("bookings.add")}
          </Button>
          {(isMobile || isTablet) && (
            <Button
              color="primary"
              variant="outlined"
              className={`${styles.addJournal} px-m py-s`}
              size="large"
              onClick={active ? handleJournalClick : undefined}
              disabled={!active}
              aria-disabled={!active}
            >
              {t("calendar.journal.add_journal")}
            </Button>
          )}
          <Button
            color="primary"
            variant="outlined"
            className={`${styles.scheduleTimeOff} px-m py-s`}
            size="large"
            onClick={active ? () => setIsScheduleTimeOffModalOpen(true) : undefined}
            disabled={!active}
            aria-disabled={!active}
          >
            {t("unavailable_days.schedule_time_off")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

Header.propTypes = {
  params: PropTypes.shape({
    date: PropTypes.string,
    range: PropTypes.string,
    weather: PropTypes.bool
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  handleJournalClick: PropTypes.func.isRequired,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired
}

export default Header
